/**
 * Light wrapper around /api/me/royalties — single-shot fetch on mount with
 * imperative refresh. We deliberately avoid React Query as a dep just for
 * this one route; the page has its own UI state for the claim flow anyway.
 */

import { useCallback, useEffect, useState } from 'react';
import { getMyRoyalties, type RoyaltyToken } from '../lib/api';

export type RoyaltiesState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | {
      kind: 'loaded';
      tokens: RoyaltyToken[];
      /** CC-077 — claim economics (nano-TON). */
      claimThresholdNano: bigint;
      claimGasNano: bigint;
    };

export function useRoyalties() {
  const [state, setState] = useState<RoyaltiesState>({ kind: 'loading' });

  const refresh = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const r = await getMyRoyalties();
      setState({
        kind: 'loaded',
        tokens: r.tokens,
        claimThresholdNano: BigInt(r.claimThresholdNano ?? '5000000000'),
        claimGasNano: BigInt(r.claimGasNano ?? '100000000'),
      });
    } catch (err: any) {
      setState({ kind: 'error', message: err?.message ?? 'failed to load royalties' });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { state, refresh };
}
